import { useQuery } from "@apollo/client"
import { FunctionComponent } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import {
    ErrorMessage,
    SyncLoaderComponent,
} from "../../../../../shared/graphQlResponse"
import styles from "./copySubscriptionToUser.module.scss"
import { getFragmentData } from "../../../../../../generated"
import { SUBSCRIPTION_FRAG_FOR_TRANSFER_SUB_TO_USER } from "../../../../../../graphql/fragments/subscription"
import { SUBJECT_FRAG_FOR_TRANSFER_SUB_TO_USER } from "../../../../../../graphql/fragments/subject"
import { useNotificationsContext } from "../../../../context/notificationsContext"
import ModalHeader from "../../../../../shared/modalHeader"
import { TrexNavigator } from "../../../../../../classes/navigator/navigator"
import CopyToUserFormContainer from "./formContainer"
import { TRANSFER_SUB_TO_USER_QRY } from "../../../../../../graphql/queries/subscriptionsTransferQuery"

const CopySubscriptionToUser: FunctionComponent = () => {
    const { key: locationKey, pathname } = useLocation()
    const navigate = useNavigate()
    const { subscriptionId: subscriptionToTransferId } = useParams()

    if (!subscriptionToTransferId) {
        throw new Error("subscriptionId not resolved from url.")
    }

    const notificationContext = useNotificationsContext()
    const { error, loading, data } = useQuery(TRANSFER_SUB_TO_USER_QRY, {
        variables: {
            subscriptionInput: { ids: [subscriptionToTransferId] },
            usersFirst: 500,
            subjectFilter: {
                activeOnly: true,
                organizationIDs: [
                    notificationContext.selectedOrganization.value,
                ],
            },
        },
    })

    const subscriptions = data?.subscriptionsForIDs.subscriptions.map((s) => {
        return getFragmentData(SUBSCRIPTION_FRAG_FOR_TRANSFER_SUB_TO_USER, s)
    })

    let copyToFormComponent = <></>
    if (loading) {
        copyToFormComponent = (
            <SyncLoaderComponent outSideDivStyle={styles.queryLoading} />
        )
    } else if (error) {
        copyToFormComponent = (
            <ErrorMessage
                outSideDivStyle={styles.queryError}
                message="There was a problem while fetching subscription information."
            />
        )
    } else {
        // get options users (for select input) that can be transferred subscriptions
        const usersOptions = data?.users.nodes
            .map((u) =>
                getFragmentData(SUBJECT_FRAG_FOR_TRANSFER_SUB_TO_USER, u)
            )
            .filter(({ identity }) => identity) // filter out users w/o identity
            .map(({ identity, id }) => ({
                label: identity?.fullName as string, // we can safely assert here because we filter out users without identity above
                value: id,
            }))

        if (!usersOptions || usersOptions.length === 0) {
            copyToFormComponent = (
                <div className={styles.queryEmpty}>
                    You do not have any users to copy subscriptions to.
                </div>
            )
        } else if (!subscriptions || subscriptions.length === 0) {
            copyToFormComponent = (
                <div className={styles.queryEmpty}>
                    You do not have any subscriptions to copy.
                </div>
            )
        } else {
            copyToFormComponent = (
                <CopyToUserFormContainer
                    subsToTransfer={subscriptions}
                    toUserOptions={usersOptions}
                />
            )
        }
    }

    const fromUserName =
        (subscriptions && subscriptions[0].subscriber.identity?.fullName) || ""

    return (
        <div className={styles.container}>
            <ModalHeader
                title="Copy Subscription to User"
                subTitle={`Copy from: ${fromUserName}`}
                trexNavigator={
                    new TrexNavigator(
                        {
                            navigateTo: -1,
                            locationKey: locationKey,
                            pathName: pathname,
                        },
                        navigate
                    )
                }
            />
            {copyToFormComponent}
        </div>
    )
}

export default CopySubscriptionToUser
